/**
 * Authentication Service for Support Portal
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ===========================================
// TYPES
// ===========================================

export interface PortalUser {
  id: string;
  email: string;
  role: string;
  displayName?: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: PortalUser;
}

export interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: PortalUser | null;
  expiresAt: number | null;
}

// ===========================================
// AUTH SERVICE
// ===========================================

class AuthService {
  private state: AuthState = {
    accessToken: null,
    refreshToken: null,
    user: null,
    expiresAt: null,
  };

  private refreshPromise: Promise<string | null> | null = null;

  constructor() {
    this.loadFromStorage();
  }

  /**
   * Register a new user
   */
  async register(email: string, password: string, displayName?: string): Promise<void> {
    const url = `${API_BASE_URL}/api/portal/auth/register`;
    console.log('[Auth] Registering user at:', url);

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, displayName }),
      });
    } catch (networkError) {
      console.error('[Auth] Network error during registration:', networkError);
      throw new Error(`Network error: Unable to connect to server. Please check your connection.`);
    }

    console.log('[Auth] Registration response status:', response.status);

    if (!response.ok) {
      const errorMessage = await this.parseErrorResponse(response, 'Registration failed');
      console.error('[Auth] Registration failed:', errorMessage);
      throw new Error(errorMessage);
    }
  }

  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<LoginResponse> {
    const url = `${API_BASE_URL}/api/portal/auth/login`;
    console.log('[Auth] Logging in at:', url);

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
    } catch (networkError) {
      console.error('[Auth] Network error during login:', networkError);
      throw new Error(`Network error: Unable to connect to server. Please check your connection.`);
    }

    console.log('[Auth] Login response status:', response.status);

    if (!response.ok) {
      const errorMessage = await this.parseErrorResponse(response, 'Login failed');
      console.error('[Auth] Login failed:', errorMessage);
      throw new Error(errorMessage);
    }

    const data: LoginResponse = await response.json();
    this.setTokens(data);
    return data;
  }

  /**
   * Parse error response safely
   */
  private async parseErrorResponse(response: Response, defaultMessage: string): Promise<string> {
    try {
      const text = await response.text();
      if (!text) return defaultMessage;
      const error = JSON.parse(text);
      return error.message || error.error || defaultMessage;
    } catch {
      return defaultMessage;
    }
  }

  /**
   * Refresh the access token
   */
  async refreshAccessToken(): Promise<string | null> {
    // Deduplicate concurrent refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    if (!this.state.refreshToken) {
      return null;
    }

    this.refreshPromise = this.doRefresh();

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async doRefresh(): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/portal/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: this.state.refreshToken }),
      });

      if (!response.ok) {
        this.logout();
        return null;
      }

      const data = await response.json();
      this.state.accessToken = data.accessToken;
      this.state.expiresAt = Date.now() + data.expiresIn * 1000;
      this.saveToStorage();
      return data.accessToken;
    } catch {
      this.logout();
      return null;
    }
  }

  /**
   * Verify email address using token from verification link
   */
  async verifyEmail(token: string): Promise<{ success: boolean; message: string; alreadyVerified?: boolean }> {
    const url = `${API_BASE_URL}/api/portal/auth/verify-email`;
    console.log('[Auth] Verifying email at:', url);

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
    } catch (networkError) {
      console.error('[Auth] Network error during email verification:', networkError);
      throw new Error('Network error: Unable to connect to server.');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Email verification failed');
    }

    return data;
  }

  /**
   * Resend verification email
   */
  async resendVerification(email: string): Promise<{ success: boolean; message: string }> {
    const url = `${API_BASE_URL}/api/portal/auth/resend-verification`;
    console.log('[Auth] Resending verification email to:', email);

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch (networkError) {
      console.error('[Auth] Network error during resend verification:', networkError);
      throw new Error('Network error: Unable to connect to server.');
    }

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || data.error || 'Failed to resend verification email');
    }

    return data;
  }

  /**
   * Logout and revoke tokens
   */
  async logout(): Promise<void> {
    if (this.state.refreshToken) {
      try {
        await fetch(`${API_BASE_URL}/api/portal/auth/logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken: this.state.refreshToken }),
        });
      } catch {
        // Ignore errors during logout
      }
    }

    this.clearState();
  }

  /**
   * Get valid access token (refreshing if needed)
   */
  async getValidAccessToken(): Promise<string | null> {
    let token = this.state.accessToken;

    // If token is expired or about to expire (within 30 seconds), refresh it
    if (!token || this.isTokenExpired()) {
      token = await this.refreshAccessToken();
    }

    return token;
  }

  /**
   * Get current access token without refreshing
   */
  getAccessToken(): string | null {
    return this.state.accessToken;
  }

  /**
   * Check if access token is expired
   */
  isTokenExpired(): boolean {
    if (!this.state.expiresAt) return true;
    // Consider token expired 30 seconds before actual expiry
    return Date.now() >= this.state.expiresAt - 30000;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.state.accessToken && !!this.state.refreshToken;
  }

  /**
   * Get current user info
   */
  getUser(): PortalUser | null {
    return this.state.user;
  }

  // ===========================================
  // PRIVATE METHODS
  // ===========================================

  private setTokens(data: LoginResponse): void {
    this.state = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      user: data.user,
      expiresAt: Date.now() + data.expiresIn * 1000,
    };
    this.saveToStorage();
  }

  private clearState(): void {
    this.state = {
      accessToken: null,
      refreshToken: null,
      user: null,
      expiresAt: null,
    };
    localStorage.removeItem('portal_auth');
    // Also clear legacy API key
    localStorage.removeItem('portal_api_key');
  }

  private saveToStorage(): void {
    localStorage.setItem('portal_auth', JSON.stringify(this.state));
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem('portal_auth');
      if (stored) {
        this.state = JSON.parse(stored);
      }
    } catch {
      this.clearState();
    }
  }
}

export const authService = new AuthService();
